import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { SurfaceOf } from './types';
import { CdsFindingView } from './CdsFindings';
import { useSurfaceActions } from './context';
import { fullName } from './format';
import { useT, useUiLanguage } from '../i18n/useT';

type Card = SurfaceOf<'confirmation-card'>;

export function ConfirmationCardSurfaceView({ surface, now = () => Date.now() }: { surface: Card; now?: () => number }): JSX.Element {
  const t = useT();
  const lang = useUiLanguage();
  const actions = useSurfaceActions();
  const [acked, setAcked] = useState<Set<string>>(() => new Set());
  const [decision, setDecision] = useState<'confirm' | 'reject' | undefined>();
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(() => Math.max(0, Math.floor((new Date(surface.expiresAt).getTime() - now()) / 1000)));

  useEffect(() => {
    const id = setInterval(() => setRemaining(Math.max(0, Math.floor((new Date(surface.expiresAt).getTime() - now()) / 1000))), 1000);
    return () => clearInterval(id);
  }, [surface.expiresAt, now]);

  const required = useMemo(() => {
    const ids = new Set(surface.requiresAcknowledgement);
    for (const f of surface.cdsFindings) if (f.requiresAcknowledgement || f.severity === 'critical') ids.add(f.findingId);
    return ids;
  }, [surface]);
  const allAcked = Array.from(required).every((id) => acked.has(id));
  const expired = remaining <= 0;
  const disabled = busy || decision !== undefined || expired;

  const decide = async (d: 'confirm' | 'reject'): Promise<void> => {
    setBusy(true);
    try {
      await actions.confirm({ pendingActionId: surface.pendingActionId, decision: d, acknowledgedFindingIds: Array.from(acked) });
      setDecision(d);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`surface surface-confirm kind-${surface.kind.toLowerCase()}`} aria-live="polite">
      <header className="confirm-header">
        <h3 className="surface-title">{surface.title}</h3>
        <span className={expired ? 'chip chip-bad' : 'chip chip-muted'}>{expired ? t('expired') : t('expiresIn', { seconds: remaining })}</span>
      </header>
      {surface.patient ? (
        <p className="confirm-patient">
          {t('patient')}: <strong>{fullName(surface.patient.name, lang === 'ar')}</strong> · {t('mrn')} {surface.patient.mrn} · {surface.patient.birthDate}
        </p>
      ) : null}
      <dl className="confirm-lines">
        {surface.lines.map((l, i) => (
          <div key={i}>
            <dt>{l.label}</dt>
            <dd>{l.value}</dd>
          </div>
        ))}
      </dl>
      {surface.cdsFindings.length > 0 ? (
        <ul className="cds-list">
          {surface.cdsFindings.map((f) => (
            <CdsFindingView key={f.findingId} finding={f}>
              {required.has(f.findingId) ? (
                <label className="ack">
                  <input
                    type="checkbox"
                    checked={acked.has(f.findingId)}
                    disabled={disabled}
                    onChange={(e) => {
                      const next = new Set(acked);
                      if (e.target.checked) next.add(f.findingId);
                      else next.delete(f.findingId);
                      setAcked(next);
                    }}
                  />
                  {t('acknowledge')}
                </label>
              ) : null}
            </CdsFindingView>
          ))}
        </ul>
      ) : null}
      <div className="confirm-actions">
        <button type="button" className="btn btn-primary" disabled={disabled || !allAcked} onClick={() => void decide('confirm')}>
          {surface.confirmLabel}
        </button>
        <button type="button" className="btn" disabled={disabled} onClick={() => void decide('reject')}>
          {surface.rejectLabel}
        </button>
        {decision ? <span className="muted small">{decision === 'confirm' ? surface.confirmLabel : surface.rejectLabel} ✓</span> : null}
        {!allAcked && !decision ? <span className="muted small">Acknowledge all critical findings to enable {surface.confirmLabel}.</span> : null}
      </div>
    </section>
  );
}
